import styled from '@emotion/styled';
import type { GetServerSidePropsResult } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';

import {
	getBookingFeeRate,
	type IBookingFeeResponse,
} from '@/clients/bookingfee';
import { getPropertyContent, type IContext } from '@/clients/content';
import { isClientError } from '@/clients/error';
import { getExperiments, getPropertyExperiments } from '@/clients/experiment';
import { BookingBar } from '@/components/booking/BookingBar';
import { BookingCard } from '@/components/booking/BookingCard';
import { GuestPicker } from '@/components/booking/GuestPicker';
import { useStayPricing } from '@/components/booking/useStayPricing';
import {
	CoverActions,
	CoverSheet,
	WideScreenOnly,
} from '@/components/layout/CoverLayout';
import { PageShell } from '@/components/layout/PageShell';
import { HelpLink, HostedBy, SiteHeader } from '@/components/layout/SiteHeader';
import { Amenities } from '@/components/property/Amenities';
import { Description } from '@/components/property/Description';
import { Gallery } from '@/components/property/Gallery';
import { HouseRules } from '@/components/property/HouseRules';
import { LocationMap } from '@/components/property/LocationMap';
import { MetaTagsHead } from '@/components/property/MetaTagsHead';
import { PropertyTitle } from '@/components/property/PropertyTitle';
import { ShareButton } from '@/components/property/ShareButton';
import { ReviewsPanel } from '@/components/property/reviews/ReviewsPanel';
import { Divider } from '@/components/ui/Divider';
import { Column, Grid } from '@/components/ui/Grid';
import { Panel } from '@/components/ui/Panel';
import { Inline, Stack } from '@/components/ui/Stack';
import { DialogDrawer } from '@/components/ui/dialog-drawer';
import { useSendDiscountPageLoadEvent } from '@/hooks/tracking/discount';
import { getAppliedDiscount } from '@/lib/discounts';
import { isGeofencedCountry } from '@/lib/geofence';
import { parseNumberQuery, parseStringQuery } from '@/lib/query';
import { getReviewStats } from '@/lib/rating';
import { datesSequential, totalGuestsLessThanCapacity } from '@/lib/stay';
import { mq, mqMax, pageGutter, spacing } from '@/lib/theme';
import { useI18n } from '@/locales';
import { withLocaleProps } from '@/locales/localeProps';
import type { IContent } from '@/types/content';
import type { IExperiments } from '@/types/experiment';

import type { NextPageWithLayout } from '../../_app';

// Mobiscroll is a browser-only widget and the calendar only opens after the
// guest taps a date field, so it never renders on the server.
const CalendarWidget = dynamic(
	() => import('@/components/booking/CalendarWidget'),
	{ ssr: false },
);

const SITE_URL = 'https://direct.travelnest.com';

const Sticky = styled.div`
	position: sticky;
	top: ${spacing.space50};

	${mqMax.lg} {
		display: none;
	}
`;

/* The panel rising over the photo on phones; below the mosaic from tablets up. */
const Body = styled(CoverSheet)`
	${mqMax.md} {
		padding: ${spacing.space50} ${pageGutter.mobile} 0;
	}

	${mq.md} {
		margin-top: ${spacing.space50};
	}
`;

const Sections = styled(Stack)`
	> hr {
		margin: ${spacing.space20} 0;
	}
`;

const loadPage = async (
	ctx: IContext,
): Promise<GetServerSidePropsResult<PropertyPageProps>> => {
	if (isGeofencedCountry(ctx)) {
		return { notFound: true };
	}

	try {
		const content = await getPropertyContent(ctx.query.propertyId);
		const [experiments, bookingFee, propertyExperiments] =
			await Promise.all([
				getExperiments(content.property.owner_id),
				getBookingFeeRate(ctx.query.propertyId),
				getPropertyExperiments(ctx.query.propertyId),
			]);
		const petFees = propertyExperiments.pet_fees === 'on';
		return { props: { content, experiments, bookingFee, petFees } };
	} catch (error) {
		console.error('Could not load property page', error);
		if (isClientError(error)) {
			return { notFound: true };
		}
		throw error;
	}
};

export const getServerSideProps = withLocaleProps(loadPage);

interface PropertyPageProps {
	content: IContent;
	experiments: IExperiments;
	bookingFee: IBookingFeeResponse;
	petFees: boolean;
}

const PropertyPage: NextPageWithLayout<PropertyPageProps> = ({
	content,
	bookingFee,
	experiments,
	petFees,
}) => {
	const t = useI18n();
	const router = useRouter();
	const { property, images = [], discounts = [], reviews } = content;

	// Dates, guests and discount codes can arrive in the link (from emails and ads).
	const queryAdults = parseNumberQuery(router.query.adults);
	const queryChildren = parseNumberQuery(router.query.children);
	const queryPets = parseNumberQuery(router.query.pets);
	const queryCheckIn = parseStringQuery(router.query.check_in);
	const queryCheckOut = parseStringQuery(router.query.check_out);
	const affiliate = parseStringQuery(router.query.affiliate);

	const pricing = useStayPricing({
		content,
		bookingFeeRate: bookingFee.booking_fee_rate,
		initialDates:
			queryCheckIn &&
			queryCheckOut &&
			datesSequential(queryCheckIn, queryCheckOut)
				? { startOn: queryCheckIn, endOn: queryCheckOut }
				: undefined,
		initialGuests:
			!Number.isNaN(queryAdults) &&
			!Number.isNaN(queryChildren) &&
			totalGuestsLessThanCapacity(
				property.capacity,
				queryAdults,
				queryChildren,
			)
				? {
						adults: queryAdults,
						children: queryChildren,
						pets: queryPets > 0 ? queryPets : undefined,
					}
				: undefined,
		initialDiscount: getAppliedDiscount(
			parseStringQuery(router.query.discount),
			discounts,
		),
		petFees,
	});
	useSendDiscountPageLoadEvent(pricing.discount);

	const [calendarOpen, setCalendarOpen] = useState(false);
	const [guestsOpen, setGuestsOpen] = useState(false);
	const [sheetOpen, setSheetOpen] = useState(false);
	const reviewsRef = useRef<HTMLDivElement>(null);

	const openCalendar = useCallback(() => setCalendarOpen(true), []);
	const closeCalendar = useCallback(() => setCalendarOpen(false), []);
	const openGuests = useCallback(() => setGuestsOpen(true), []);

	const reserve = useCallback(() => {
		const { stay, discount } = pricing;
		const params = new URLSearchParams({
			adults: String(stay.adults),
			children: String(stay.children),
			check_in: stay.startDate ?? '',
			check_out: stay.endDate ?? '',
		});
		if (stay.pets) params.set('pets', String(stay.pets));
		if (discount) params.set('discount', discount.discountCode);
		if (affiliate) params.set('affiliate', affiliate);
		void router.push(
			`/properties/${property.id}/guest-details?${params.toString()}`,
		);
	}, [pricing, affiliate, property.id, router]);

	const showReviews =
		experiments?.direct_reviews === 'on' && reviews.reviews.length > 0;
	const stats = showReviews ? getReviewStats(reviews.reviews) : null;

	const bookingCard = (
		<BookingCard
			pricing={pricing}
			cancellationPolicy={
				property.cancellation.tn_cancellation_preference
			}
			securityDeposit={property.security_deposit}
			onOpenCalendar={openCalendar}
			onOpenGuests={openGuests}
			onReserve={reserve}
		/>
	);

	return (
		<>
			<MetaTagsHead
				property={property}
				images={images}
				url={`${SITE_URL}/properties/${property.id}`}
			/>

			<CoverActions>
				<PropertyHeaderEnd content={content} />
			</CoverActions>
			<Gallery images={images} propertyName={property.name} />

			<Body>
				<Grid>
					<Column span={{ base: 12, lg: 7, xl: 8 }}>
						<Sections gap="space50">
							<PropertyTitle
								property={property}
								rating={
									stats
										? {
												average: stats.averageRating,
												count: stats.count,
											}
										: null
								}
								reviewsRef={reviewsRef}
							/>
							<Divider />
							<Description text={property.long_description} />
							<Divider />
							<Amenities
								amenities={property.formatted_amenities}
							/>
							<Divider />
							<HouseRules
								restrictions={property.restrictions}
								checkIn={property.check_in}
								checkOut={property.check_out}
							/>
							<Divider />
							<LocationMap property={property} />
							{showReviews && (
								<>
									<Divider />
									<ReviewsPanel
										ref={reviewsRef}
										reviews={reviews.reviews}
									/>
								</>
							)}
						</Sections>
					</Column>
					<Column span={{ base: 12, lg: 5, xl: 4 }}>
						<Sticky>
							<Panel padding="lg">{bookingCard}</Panel>
						</Sticky>
					</Column>
				</Grid>
			</Body>

			<BookingBar
				pricing={pricing}
				cancellationPolicy={
					property.cancellation.tn_cancellation_preference
				}
				onOpenSheet={() => setSheetOpen(true)}
				onOpenCalendar={openCalendar}
				onReserve={reserve}
			/>
			<DialogDrawer
				open={sheetOpen}
				onOpenChange={setSheetOpen}
				title={t('booking.title')}
				closeLabel={t('common.close')}
			>
				{bookingCard}
			</DialogDrawer>

			<CalendarWidget
				open={calendarOpen}
				onClose={closeCalendar}
				los={pricing.los}
				firstSelectableDate={pricing.firstSelectableDate}
				onChange={pricing.setDates}
			/>
			<GuestPicker
				open={guestsOpen}
				onClose={() => setGuestsOpen(false)}
				maxGuests={property.capacity}
				maxPets={pricing.maxPets}
				guests={pricing.guests}
				onChange={pricing.setGuests}
			/>
		</>
	);
};

/** Share and Help: in the header from tablets up, over the photo on phones. */
const PropertyHeaderEnd = ({ content }: Pick<PropertyPageProps, 'content'>) => {
	const property = content?.property;
	return (
		<Inline gap="space20" wrap={false}>
			{property && (
				<ShareButton
					title={property.name}
					text={
						property.summary_description ||
						property.long_description.slice(0, 150)
					}
					url={`${SITE_URL}/properties/${property.id}`}
				/>
			)}
			<HelpLink propertyId={property?.id} />
		</Inline>
	);
};

PropertyPage.getLayout = (page) => (
	<PageShell
		header={
			<WideScreenOnly>
				<SiteHeader
					start={<HostedBy />}
					end={
						<PropertyHeaderEnd
							content={(page.props as PropertyPageProps).content}
						/>
					}
				/>
			</WideScreenOnly>
		}
		bottomSpace
		white
	>
		{page}
	</PageShell>
);

export default PropertyPage;
